import { useCallback, useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import Link from "next/link";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type Connection,
  type NodeTypes,
  type NodeProps,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  Handle,
  Position,
  MarkerType,
} from "@xyflow/react";

import "@xyflow/react/dist/style.css";
import {
  ArrowsMergeIcon,
  ChatIcon,
  ChatsIcon,
  CheckIcon,
  DnaIcon,
  EnvelopeIcon,
  FileLockIcon,
  HandshakeIcon,
  Icon,
  IdentificationCardIcon,
  KeyIcon,
  LightningIcon,
  LockKeyIcon,
  LockLaminatedOpenIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  NotePencilIcon,
  PackageIcon,
  PlantIcon,
  ShieldIcon,
  ShieldCheckIcon,
  UserIcon,
  WifiHighIcon,
  AddressBookIcon,
} from "@phosphor-icons/react";
import { InboxIcon } from "lucide-react";
import Image from "next/image";

// ─── Google Fonts ─────────────────────────────────────────────────────────────
const GlobalFonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&display=swap');
`;

// ─── Design Tokens ────────────────────────────────────────────────────────────
const COLORS = {
  bg: "#110e08",
  surface: "#1f1c11",
  border: "#794509",
  accent: {
    orange: "#ea7f05",
    cyan: "#00cfcf",
    emerald: "#00c896",
    amber: "#f5a623",
    rose: "#f04070",
    sky: "#38bdf8",
    gold: "#d4af37",
    violet: "#a78bfa",
  },
  text: {
    primary: "#f8eee2",
    muted: "#a09170",
    dim: "#706145",
  },
} as const;

type AccentKey = keyof typeof COLORS.accent;

// ─── Variant Styles ───────────────────────────────────────────────────────────
const VARIANTS = {
  genesis: {
    bg: "linear-gradient(135deg, #30210d 0%, #30210d 100%)",
    border: COLORS.accent.orange,
    glow: `0 0 24px 3px ${COLORS.accent.orange}44`,
    label: COLORS.accent.orange,
  },
  keygen: {
    bg: "linear-gradient(135deg, #0d1a30 0%, #001820 100%)",
    border: COLORS.accent.cyan,
    glow: `0 0 18px 2px ${COLORS.accent.cyan}33`,
    label: COLORS.accent.cyan,
  },
  account: {
    bg: "linear-gradient(135deg, #001820 0%, #0a1a10 100%)",
    border: COLORS.accent.emerald,
    glow: `0 0 18px 2px ${COLORS.accent.emerald}33`,
    label: COLORS.accent.emerald,
  },
  send: {
    bg: "linear-gradient(135deg, #1a1000 0%, #1a0010 100%)",
    border: COLORS.accent.amber,
    glow: `0 0 16px 2px ${COLORS.accent.amber}33`,
    label: COLORS.accent.amber,
  },
  encrypt: {
    bg: "linear-gradient(135deg, #200010 0%, #10001a 100%)",
    border: COLORS.accent.rose,
    glow: `0 0 20px 3px ${COLORS.accent.rose}33`,
    label: COLORS.accent.rose,
  },
  transport: {
    bg: "linear-gradient(135deg, #001020 0%, #001028 100%)",
    border: COLORS.accent.sky,
    glow: `0 0 16px 2px ${COLORS.accent.sky}33`,
    label: COLORS.accent.sky,
  },
  decrypt: {
    bg: "linear-gradient(135deg, #1a1200 0%, #1a0a00 100%)",
    border: COLORS.accent.gold,
    glow: `0 0 20px 3px ${COLORS.accent.gold}44`,
    label: COLORS.accent.gold,
  },
  end: {
    bg: "linear-gradient(135deg, #0a1a0a 0%, #0d200d 100%)",
    border: COLORS.accent.emerald,
    glow: `0 0 24px 4px ${COLORS.accent.emerald}44`,
    label: COLORS.accent.emerald,
  },
  thread: {
    bg: "linear-gradient(135deg, #130a1f 0%, #0d0018 100%)",
    border: COLORS.accent.violet,
    glow: `0 0 20px 3px ${COLORS.accent.violet}33`,
    label: COLORS.accent.violet,
  },
} as const;

type VariantKey = keyof typeof VARIANTS;

// ─── Node Data Types ──────────────────────────────────────────────────────────
export type MajikNodeData = Record<string, unknown> & {
  label: string;
  description?: string;
  icon?: Icon;
  step?: string;
  variant?: VariantKey;
  hideTarget?: boolean;
  hideSource?: boolean;
  example?: string;
  docLink?: string;
  docLabel?: string;
};

export type SectionNodeData = Record<string, unknown> & {
  label: string;
  color?: string;
};

export type MajikFlowNode = Node<MajikNodeData, "majikNode">;
export type SectionFlowNode = Node<SectionNodeData, "sectionLabel">;
export type AnyFlowNode = MajikFlowNode | SectionFlowNode;

// ─── Layout Engine ────────────────────────────────────────────────────────────
const NODE_H = 320;
const SECTION_H = 40;
const BRANCH_EXPAND = 15;
const GAP_DEFAULT = 80;

const COL = {
  left: 60,
  center: 340,
  right: 620,
} as const;

const COL_MOBILE = {
  left: 20,
  center: 160,
  right: 300,
} as const;

type LayoutEntry =
  | { type: "section"; id: string }
  | { type: "node"; id: string; col?: keyof typeof COL }
  | { type: "branch"; left: string; right: string }
  | { type: "gap"; px?: number };

function layoutNodes(
  entries: LayoutEntry[],
  rawNodes: AnyFlowNode[],
  isMobile: boolean = false,
): AnyFlowNode[] {
  const positioned = new Map<string, { x: number; y: number }>();
  let cursor = 0;

  const cols = isMobile ? COL_MOBILE : COL;

  for (const entry of entries) {
    if (entry.type === "gap") {
      cursor += entry.px ?? GAP_DEFAULT;
      continue;
    }

    if (entry.type === "section") {
      positioned.set(entry.id, { x: cols.left, y: cursor });
      cursor += SECTION_H;
      continue;
    }

    if (entry.type === "node") {
      const col = entry.col ?? "center";
      positioned.set(entry.id, { x: cols[col], y: cursor });
      cursor += NODE_H;
      continue;
    }

    if (entry.type === "branch") {
      cursor += BRANCH_EXPAND;
      positioned.set(entry.left, { x: cols.left, y: cursor });
      positioned.set(entry.right, { x: cols.right, y: cursor });
      cursor += NODE_H;
      continue;
    }
  }

  return rawNodes.map((node) => {
    const pos = positioned.get(node.id);
    if (!pos) return node;
    return { ...node, position: pos };
  });
}

// ─── Styled Components ────────────────────────────────────────────────────────

const RootContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors?.primaryBackground ?? COLORS.bg};
  overflow-y: auto;
  overflow-x: hidden;
`;

const CanvasLayer = styled.div<{ $height?: number }>`
  flex: 1 1 0;
  position: relative;
  ${({ $height }) =>
    $height != null ? `height: ${$height}px; flex: none;` : ""}
`;

const Header = styled.div`
  position: relative;
  z-index: 10;
  padding: 18px 28px;
  background: ${COLORS.bg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${COLORS.border};
  flex-shrink: 0;
  pointer-events: none;

  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 10px;
  }
`;

const BrandTitle = styled.div`
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  background: ${({ theme }) =>
    theme.gradients?.primary ??
    `linear-gradient(90deg, ${COLORS.accent.orange}, ${COLORS.accent.amber})`};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BrandSubtitle = styled.div`
  font-size: 11px;
  color: ${COLORS.text.muted};
  margin-top: 2px;
  letter-spacing: 0.1em;

  @media (max-width: 768px) {
    font-size: 9px;
    letter-spacing: 0.05em;
  }
`;

const LegendRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: flex-end;

  @media (max-width: 768px) {
    gap: 8px;
    justify-content: flex-start;
    width: 100%;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  box-shadow: 0 0 6px 1px ${({ $color }) => $color}88;
`;

const LegendLabel = styled.span`
  font-size: 10px;
  color: ${COLORS.text.muted};
  letter-spacing: 0.08em;
`;

const NodeCard = styled.div<{
  $bg: string;
  $border: string;
  $glow: string;
  $hovered: boolean;
}>`
  background: ${({ $bg }) => $bg};
  border: 1.5px solid ${({ $border }) => $border};
  box-shadow: ${({ $hovered, $glow }) =>
    $hovered ? $glow : "0 2px 12px rgba(0,0,0,0.5)"};
  border-radius: 14px;
  padding: 22px 18px 16px;
  min-width: 200px;
  max-width: 260px;
  transition:
    box-shadow 0.25s,
    transform 0.15s;
  transform: ${({ $hovered }) => ($hovered ? "scale(1.03)" : "scale(1)")};
  position: relative;
  cursor: default;
`;

const StepBadge = styled.span<{ $bg: string }>`
  position: absolute;
  top: -11px;
  left: 14px;
  background: ${({ $bg }) => $bg};
  color: #0a0a14;
  font-size: 9px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const NodeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const NodeIcon = styled.span`
  font-size: 20px;
  line-height: 1;
  margin-top: 1px;
  flex-shrink: 0;
`;

const NodeLabel = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-weight: 700;
  font-size: 13px;
  line-height: 1.3;
  margin-bottom: 5px;
  letter-spacing: 0.01em;
`;

const NodeDesc = styled.div`
  color: ${COLORS.text.muted};
  font-size: 11px;
  line-height: 1.6;
`;

const ExampleChip = styled.div<{ $color: string }>`
  margin-top: 9px;
  padding: 5px 9px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid ${({ $color }) => $color}44;
  border-radius: 6px;
  font-family: "IBM Plex Mono", monospace;
  font-size: 9.5px;
  color: ${({ $color }) => $color}cc;
  word-break: break-all;
  line-height: 1.5;
`;

const ExampleLabel = styled.span`
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${COLORS.text.dim};
  display: block;
  margin-bottom: 3px;
`;

const DocLink = styled(Link)<{ $color: string }>`
  display: inline-block;
  margin-top: 8px;
  font-size: 9.5px;
  color: ${({ $color }) => $color}bb;
  text-decoration: none;
  border-bottom: 1px dotted ${({ $color }) => $color}55;
  letter-spacing: 0.06em;
  pointer-events: all;
  cursor: pointer;
  transition:
    color 0.15s,
    border-color 0.15s;

  &:hover {
    color: ${({ $color }) => $color};
    border-color: ${({ $color }) => $color}99;
  }
`;

const SectionLabelDiv = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-left: 2px solid ${({ $color }) => $color};
  opacity: 0.75;
  pointer-events: none;
  user-select: none;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
`;

const Logo = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${({ theme }) => theme.colors?.primaryBackground ?? COLORS.bg};

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const ResponsiveImage = styled(Image)`
  user-select: none;
  @media (max-width: 768px) {
    transform: scale(0.7);
  }
`;

// ─── Mobile List Styles ───────────────────────────────────────────────────────

const MobileScrollContainer = styled.div`
  width: 100%;
  background: ${COLORS.bg};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
`;

const MobileSectionHeader = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 20px 16px 8px;
  border-left: 2px solid ${({ $color }) => $color};
  margin: 0 16px;
  opacity: 0.85;
`;

const MobileCardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 16px;
`;

const MobileBranchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const MobileNodeCard = styled.div<{
  $bg: string;
  $border: string;
  $glow: string;
}>`
  background: ${({ $bg }) => $bg};
  border: 1.5px solid ${({ $border }) => $border};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  border-radius: 14px;
  padding: 18px 14px 14px;
  position: relative;
  transition: box-shadow 0.25s;

  &:active {
    box-shadow: ${({ $glow }) => $glow};
  }
`;

const MobileConnector = styled.div<{ $color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: linear-gradient(
      to bottom,
      ${({ $color }) => $color}88,
      ${({ $color }) => $color}44
    );
  }

  &::after {
    content: "▼";
    font-size: 8px;
    color: ${({ $color }) => $color}88;
    position: relative;
    z-index: 1;
    background: ${COLORS.bg};
    padding: 0 4px;
  }
`;

const MobileSectionGap = styled.div`
  height: 8px;
`;

// ─── Custom Node Components ───────────────────────────────────────────────────

function MajikNode({ data }: NodeProps<MajikFlowNode>) {
  const variant: VariantKey =
    (data.variant as VariantKey | undefined) ?? "account";
  const theme = VARIANTS[variant];
  const [hovered, setHovered] = useState(false);
  const NodeIconComp = data.icon as Icon;

  return (
    <NodeCard
      $bg={theme.bg}
      $border={theme.border}
      $glow={theme.glow}
      $hovered={hovered}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!data.hideTarget && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: theme.border,
            border: `2px solid ${theme.border}`,
            width: 10,
            height: 10,
          }}
        />
      )}
      {data.step && (
        <StepBadge $bg={theme.border}>{data.step as string}</StepBadge>
      )}
      <NodeRow>
        {data.icon && (
          <NodeIcon>
            <NodeIconComp size={16} weight="fill" color={theme.label} />
          </NodeIcon>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <NodeLabel $color={theme.label}>{data.label}</NodeLabel>
          {data.description && (
            <NodeDesc>{data.description as string}</NodeDesc>
          )}
          {data.example && (
            <ExampleChip $color={theme.label}>
              <ExampleLabel>Example</ExampleLabel>
              {data.example as string}
            </ExampleChip>
          )}
          {data.docLink && (
            <DocLink
              href={data.docLink as string}
              target="_blank"
              rel="noopener noreferrer"
              $color={theme.label}
            >
              {(data.docLabel as string | undefined) ?? "Learn more →"}
            </DocLink>
          )}
        </div>
      </NodeRow>
      {!data.hideSource && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: theme.border,
            border: `2px solid ${theme.border}`,
            width: 10,
            height: 10,
          }}
        />
      )}
    </NodeCard>
  );
}

function SectionLabel({ data }: NodeProps<SectionFlowNode>) {
  const color = (data.color as string | undefined) ?? COLORS.text.dim;
  return <SectionLabelDiv $color={color}>{data.label}</SectionLabelDiv>;
}

const NODE_TYPES: NodeTypes = {
  majikNode: MajikNode as unknown as NodeTypes[string],
  sectionLabel: SectionLabel as unknown as NodeTypes[string],
};

// ─── Mobile Card Renderer ─────────────────────────────────────────────────────

function MobileCard({ nodeData }: { nodeData: MajikNodeData }) {
  const variant: VariantKey = nodeData.variant ?? "account";
  const theme = VARIANTS[variant];
  const NodeIconComp = nodeData.icon as Icon | undefined;

  return (
    <MobileNodeCard $bg={theme.bg} $border={theme.border} $glow={theme.glow}>
      {nodeData.step && (
        <StepBadge $bg={theme.border}>{nodeData.step}</StepBadge>
      )}
      <NodeRow>
        {NodeIconComp && (
          <NodeIcon>
            <NodeIconComp size={16} weight="fill" color={theme.label} />
          </NodeIcon>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <NodeLabel $color={theme.label}>{nodeData.label}</NodeLabel>
          {nodeData.description && <NodeDesc>{nodeData.description}</NodeDesc>}
          {nodeData.example && (
            <ExampleChip $color={theme.label}>
              <ExampleLabel>Example</ExampleLabel>
              {nodeData.example}
            </ExampleChip>
          )}
          {nodeData.docLink && (
            <DocLink
              href={nodeData.docLink}
              target="_blank"
              rel="noopener noreferrer"
              $color={theme.label}
            >
              {nodeData.docLabel ?? "Learn more →"}
            </DocLink>
          )}
        </div>
      </NodeRow>
    </MobileNodeCard>
  );
}

// ─── Mobile Layout ────────────────────────────────────────────────────────────

// Maps section label colors to their accent key for connectors
const SECTION_CONNECTOR_COLORS: Record<string, string> = {
  "① Account Setup": COLORS.accent.orange,
  "② Key Derivation": COLORS.accent.cyan,
  "③ Identity & Address": COLORS.accent.emerald,
  "④ Compose & Send": COLORS.accent.amber,
  "⑤ Post-Quantum Encryption": COLORS.accent.rose,
  "⑥ Transport": COLORS.accent.sky,
  "⑦ Post-Quantum Decryption": COLORS.accent.gold,
};

function MobileLayout({ rawNodes }: { rawNodes: AnyFlowNode[] }) {
  // Build a lookup map for node data by id
  const nodeById = new Map<string, AnyFlowNode>(rawNodes.map((n) => [n.id, n]));

  // Render the layout entries as mobile-friendly stacked cards
  const rendered: React.ReactNode[] = [];
  let currentColor: string = COLORS.accent.orange as string;
  let prevWasBranch = false;
  let prevWasSection = false;

  for (let i = 0; i < LAYOUT.length; i++) {
    const entry = LAYOUT[i];

    if (entry.type === "section") {
      const node = nodeById.get(entry.id) as SectionFlowNode | undefined;
      if (!node) continue;
      const color =
        SECTION_CONNECTOR_COLORS[node.data.label] ??
        (node.data.color as string) ??
        COLORS.text.dim;
      currentColor = color;

      if (i > 0 && !prevWasSection) {
        rendered.push(<MobileSectionGap key={`gap-before-${entry.id}`} />);
      }

      rendered.push(
        <MobileSectionHeader key={entry.id} $color={color}>
          {node.data.label}
        </MobileSectionHeader>,
      );
      prevWasBranch = false;
      prevWasSection = true;
      continue;
    }

    if (entry.type === "gap") {
      rendered.push(
        <MobileSectionGap key={`gap-${i}`} style={{ height: 4 }} />,
      );
      prevWasBranch = false;
      prevWasSection = false;
      continue;
    }

    if (entry.type === "node") {
      const node = nodeById.get(entry.id) as MajikFlowNode | undefined;
      if (!node) continue;

      if (!prevWasSection && rendered.length > 0 && !prevWasBranch) {
        rendered.push(
          <MobileConnector key={`conn-${entry.id}`} $color={currentColor} />,
        );
      } else if (prevWasBranch) {
        rendered.push(
          <MobileConnector key={`conn-${entry.id}`} $color={currentColor} />,
        );
      }

      rendered.push(
        <MobileCardGrid key={entry.id}>
          <MobileCard nodeData={node.data} />
        </MobileCardGrid>,
      );
      prevWasBranch = false;
      prevWasSection = false;
      continue;
    }

    if (entry.type === "branch") {
      const leftNode = nodeById.get(entry.left) as MajikFlowNode | undefined;
      const rightNode = nodeById.get(entry.right) as MajikFlowNode | undefined;

      if (!prevWasSection && rendered.length > 0) {
        rendered.push(
          <MobileConnector
            key={`conn-branch-${entry.left}`}
            $color={currentColor}
          />,
        );
      }

      rendered.push(
        <MobileCardGrid key={`branch-${entry.left}-${entry.right}`}>
          <MobileBranchRow>
            {leftNode && <MobileCard nodeData={leftNode.data} />}
            {rightNode && <MobileCard nodeData={rightNode.data} />}
          </MobileBranchRow>
        </MobileCardGrid>,
      );
      prevWasBranch = true;
      prevWasSection = false;
      continue;
    }
  }

  return (
    <MobileScrollContainer>
      <div style={{ paddingBottom: 40 }}>{rendered}</div>
    </MobileScrollContainer>
  );
}

// ─── Raw Node Definitions ─────────────────────────────────────────────────────

const RAW_NODES: AnyFlowNode[] = [
  // ① Account Setup
  {
    id: "sec-onboard",
    type: "sectionLabel",
    position: { x: 0, y: 0 },
    data: { label: "① Account Setup", color: COLORS.accent.orange },
    selectable: false,
    draggable: false,
  },
  {
    id: "n-start",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "New User",
      icon: UserIcon,
      step: "Start",
      variant: "genesis",
      hideTarget: true,
      description:
        "You open Majik Message for the first time. No account, no server, no internet needed yet — everything happens on your device.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/accounts-documentation",
      docLabel: "Accounts documentation →",
    },
  },
  {
    id: "n-choose",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Create or Restore Account",
      icon: ArrowsMergeIcon,
      step: "Decision",
      variant: "genesis",
      description:
        "Pick a display name and a passphrase (like a password). Then choose: start fresh with a new seed phrase, or restore an existing account you've backed up.",
    },
  },
  {
    id: "n-seed",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Generate 12-Word Seed Phrase",
      icon: PlantIcon,
      step: "New",
      variant: "keygen",
      description:
        "Your app generates 12 random words — your master key. These words can restore your account on any device. Never share them.",
      example:
        "witch collapse practice feed shame open despair creek road again ice least",
      docLink: "https://github.com/paulmillr/scure-bip39",
      docLabel: "BIP-39 standard (scure/bip39) →",
    },
  },
  {
    id: "n-seed-backup",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Save Your Backup",
      icon: CheckIcon,
      variant: "keygen",
      description:
        "Write down your 12 words or save the exported JSON file. Majik Message keeps no copy — this is your only recovery method if you lose access.",
    },
  },
  {
    id: "n-import",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Import Existing Seed",
      icon: KeyIcon,
      step: "Restore",
      variant: "keygen",
      description:
        "Paste your 12/24-word phrase or load a previously exported JSON backup file to restore an account you already own. Old accounts are automatically upgraded to post-quantum security.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/accounts-documentation#import",
      docLabel: "How to import →",
    },
  },
  {
    id: "n-import-verify",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Verify Backup & Upgrade",
      icon: LockOpenIcon,
      variant: "keygen",
      description:
        "The app checks your passphrase against the encrypted backup. If valid, your account is automatically upgraded to Argon2id KDF + ML-KEM-768 post-quantum keys in one step.",
    },
  },

  // ② Key Derivation
  {
    id: "sec-keygen",
    type: "sectionLabel",
    position: { x: 0, y: 0 },
    data: { label: "② Key Derivation", color: COLORS.accent.cyan },
    selectable: false,
    draggable: false,
  },
  {
    id: "n-mnemonic-seed",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Words → 64-Byte Cryptographic Seed",
      icon: DnaIcon,
      step: "Key Gen",
      variant: "keygen",
      description:
        "Your 12 words are mathematically converted into a 64-byte secret number via BIP-39 PBKDF2-SHA512. This seed deterministically generates both classical and post-quantum keypairs.",
      example: "seed[0..64]: a3f8c1d4e2b7... (64 bytes, hex)",
      docLink: "https://github.com/paulmillr/scure-bip39#mnemonictoseedsync",
      docLabel: "mnemonicToSeedSync →",
    },
  },
  {
    id: "n-dual-derivation",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Dual Keypair Derivation",
      icon: LightningIcon,
      step: "Hybrid",
      variant: "keygen",
      description:
        "Two keypairs are derived from the same seed: (1) X25519 from seed[0..32] for fingerprint & legacy compatibility, (2) ML-KEM-768 from the full seed[0..64] for post-quantum encryption. Both are deterministic — same mnemonic always produces the same keys.",
      docLink: "https://github.com/paulmillr/noble-post-quantum",
      docLabel: "@noble/post-quantum (ML-KEM) →",
    },
  },
  {
    id: "n-x25519-path",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "X25519: Identity & Fingerprint",
      icon: IdentificationCardIcon,
      step: "Classical",
      variant: "keygen",
      description:
        "seed[0..32] → Ed25519 signing keypair → converted to X25519 encryption keypair via ed2curve. Your unique fingerprint (SHA-256 of X25519 public key) serves as your account ID.",
      docLink:
        "https://github.com/StableLib/stablelib/tree/master/packages/x25519",
      docLabel: "@stablelib/x25519 →",
    },
  },
  {
    id: "n-mlkem-path",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "ML-KEM-768: Post-Quantum Keys",
      icon: ShieldCheckIcon,
      step: "Quantum-Safe",
      variant: "keygen",
      description:
        "The full 64-byte seed is fed directly into ML-KEM-768 key generation. Produces a 1184-byte public key and 2400-byte secret key. Quantum computers cannot break this — it's based on lattice cryptography (FIPS-203).",
      example: "mlKemPublicKey: 1184 bytes, mlKemSecretKey: 2400 bytes",
      docLink: "https://csrc.nist.gov/pubs/fips/203/final",
      docLabel: "FIPS-203 standard →",
    },
  },
  {
    id: "n-argon2id-encrypt",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Argon2id: Lock Both Keys",
      icon: LockKeyIcon,
      step: "Secure Storage",
      variant: "keygen",
      description:
        "Both private keys (X25519 + ML-KEM-768) are encrypted with your passphrase using Argon2id — a modern, memory-hard KDF resistant to GPU/ASIC attacks (64 MB memory, 4 threads, 3 iterations). One passphrase unlocks both keys. Stored in IndexedDB, never leaves your device.",
      example: "Argon2id(passphrase, salt_32) → AES-256-GCM × 2 keys",
      docLink: "https://github.com/paulmillr/noble-hashes#argon2",
      docLabel: "@noble/hashes/argon2 →",
    },
  },

  // ③ Identity
  {
    id: "sec-identity",
    type: "sectionLabel",
    position: { x: 0, y: 0 },
    data: { label: "③ Identity & Address", color: COLORS.accent.emerald },
    selectable: false,
    draggable: false,
  },
  {
    id: "n-fingerprint",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Your Unique Address (Fingerprint)",
      icon: IdentificationCardIcon,
      step: "Identity",
      variant: "account",
      description:
        "A unique ID is derived from your X25519 public key using SHA-256 hashing. This is your address — what others use to send you encrypted messages. It's also your account ID across devices.",
      example: "fingerprint: xK9mR2vL8nP4qW1... (base64, 44 chars)",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/accounts-documentation#fingerprint",
      docLabel: "How fingerprints work →",
    },
  },
  {
    id: "n-contact-card",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Contact Card: Dual Public Keys",
      icon: AddressBookIcon,
      variant: "account",
      description:
        "Your shareable contact card now contains BOTH public keys: X25519 (32 bytes, for fingerprint lookup) and ML-KEM-768 (1184 bytes, for quantum-safe encryption). Recipients need the ML-KEM key to send you v3 envelopes.",
      example: "{ id, label, publicKey, fingerprint, mlKemPublicKey }",
    },
  },
  {
    id: "n-local-ready",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Account Ready — Works Offline",
      icon: LockKeyIcon,
      variant: "account",
      description:
        "Your account is fully set up with post-quantum security and usable without any internet connection. You can encrypt and decrypt messages on the Message tab right now.",
    },
  },
  {
    id: "n-register",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Register Online (Optional)",
      icon: WifiHighIcon,
      variant: "account",
      description:
        "To use live chats or threads, register your account on Majikah's network. This publishes BOTH your public keys so others can find you and send quantum-safe messages. Up to 5 accounts per Majikah login.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/accounts-documentation#online",
      docLabel: "Register on Majikah →",
    },
  },

  // ④ Compose
  {
    id: "sec-compose",
    type: "sectionLabel",
    position: { x: 0, y: 0 },
    data: { label: "④ Compose & Send", color: COLORS.accent.amber },
    selectable: false,
    draggable: false,
  },
  {
    id: "n-compose",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Write Your Message",
      icon: NotePencilIcon,
      step: "Compose",
      variant: "send",
      description:
        "Type your message. Text is automatically compressed before encryption to save bandwidth. You (the sender) are always added as a recipient so you can re-read your own sent messages.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/message-local-documentation",
      docLabel: "Message documentation →",
    },
  },
  {
    id: "n-resolve",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Resolve Recipients' ML-KEM Keys",
      icon: MagnifyingGlassIcon,
      variant: "send",
      description:
        "The app looks up each recipient's ML-KEM-768 public key (1184 bytes) from your local contact directory. One recipient triggers solo encryption; two or more triggers group encryption.",
    },
  },

  // ⑤ Encryption
  {
    id: "sec-encrypt",
    type: "sectionLabel",
    position: { x: 0, y: 0 },
    data: {
      label: "⑤ Post-Quantum Encryption",
      color: COLORS.accent.rose,
    },
    selectable: false,
    draggable: false,
  },
  {
    id: "n-envelope-version",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Envelope v3: ML-KEM Only",
      icon: ShieldCheckIcon,
      step: "v3",
      variant: "encrypt",
      description:
        "All new messages use envelope format v3 — ML-KEM-768 post-quantum key encapsulation exclusively. Legacy v1/v2 (X25519 ephemeral key exchange) have been fully deprecated.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/message-local-documentation#v3",
      docLabel: "v3 envelope format →",
    },
  },
  {
    id: "n-solo-mlkem",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Solo: ML-KEM Encapsulation",
      icon: LightningIcon,
      step: "1-to-1",
      variant: "encrypt",
      description:
        "ML-KEM-768 encapsulation generates a 32-byte shared secret + 1088-byte ciphertext. The shared secret is used directly as the AES-256-GCM key. Only the holder of the matching ML-KEM secret key can recover it.",
      example:
        "mlKemEncapsulate(recipientPubKey) → { sharedSecret_32, cipherText_1088 }",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/message-local-documentation#solo",
      docLabel: "Solo ML-KEM encryption →",
    },
  },
  {
    id: "n-solo-encrypt",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "AES-256-GCM: Encrypt Message",
      icon: LockKeyIcon,
      variant: "encrypt",
      description:
        "The 32-byte ML-KEM shared secret is used directly as the AES-256-GCM key. Message is encrypted with a random 12-byte IV + 16-byte authentication tag. Result: { iv, ciphertext, mlKemCipherText }",
      example: "aes256gcm.seal(sharedSecret, iv, plaintext) → ciphertext + tag",
    },
  },
  {
    id: "n-group-aeskey",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Group: Random AES Key",
      icon: ShieldIcon,
      step: "Group",
      variant: "encrypt",
      description:
        "A single random 32-byte key encrypts the message payload once. This key is then individually sealed for each recipient using ML-KEM-768 encapsulation.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/message-local-documentation#group",
      docLabel: "Group ML-KEM encryption →",
    },
  },
  {
    id: "n-group-perrecip",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Per-Recipient ML-KEM Sealing",
      icon: LockKeyIcon,
      variant: "encrypt",
      description:
        "For each recipient: (1) ML-KEM encapsulation generates a 32-byte shared secret, (2) group AES key is XORed with the shared secret (one-time pad), (3) result stored as encryptedAesKey. Each recipient gets their own 1088-byte ML-KEM ciphertext.",
      example:
        "encryptedAesKey = groupKey XOR mlKemSharedSecret (per recipient)",
    },
  },
  {
    id: "n-aes-encrypt",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "AES-256-GCM: Encrypt & Seal",
      icon: ShieldCheckIcon,
      step: "E2EE",
      variant: "encrypt",
      description:
        "Your message is encrypted with AES-256-GCM — the same standard used by banks and militaries. A 16-byte tamper-proof seal (AEAD tag) is appended. If anyone modifies the message in transit, decryption will fail.",
      example: "gcm.seal(iv12, plaintext) → ciphertext + 16-byte auth tag",
      docLink:
        "https://github.com/StableLib/stablelib/tree/master/packages/gcm",
      docLabel: "@stablelib/gcm →",
    },
  },

  // ⑥ Transport
  {
    id: "sec-transport",
    type: "sectionLabel",
    position: { x: 0, y: 0 },
    data: { label: "⑥ Transport", color: COLORS.accent.sky },
    selectable: false,
    draggable: false,
  },
  {
    id: "n-envelope",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Package the Envelope",
      icon: PackageIcon,
      step: "Transport",
      variant: "transport",
      description:
        "The encrypted payload is wrapped into a self-describing v3 envelope: [1-byte version=3][32-byte fingerprint][JSON payload]. The entire envelope is base64-encoded into a portable scanner string.",
      example: "~*$MJKMSG:AwB4vLm2Kp9X...== (v3 envelope, 1088+ bytes)",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/message-local-documentation",
      docLabel: "MajikEnvelope format →",
    },
  },
  {
    id: "n-route-relay",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Send It Yourself (Any Channel)",
      icon: EnvelopeIcon,
      step: "Offline",
      variant: "transport",
      description:
        "Copy and paste the encrypted string into any app — email, iMessage, WhatsApp, Telegram, a forum post, anywhere. The message is quantum-resistant gibberish to anyone who intercepts it. Only the intended recipient with the matching ML-KEM secret key can decrypt it.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/message-local-documentation",
      docLabel: "Offline messaging guide →",
    },
  },
  {
    id: "n-route-p2p",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Send via Majikah",
      icon: ChatIcon,
      step: "Online",
      variant: "transport",
      description:
        "Messages are relayed through Majikah's servers — but they're quantum-safe encrypted before they leave your device. Even Majikah's servers cannot read your messages. Chats expire after 24 hours. Threads are permanent.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/chats-realtime-documentation",
      docLabel: "Chats & Threads documentation →",
    },
  },
  {
    id: "n-threads",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Threads — Permanent Record",
      icon: FileLockIcon,
      step: "Threads",
      variant: "thread",
      description:
        "Threads are immutable conversation logs — no one can edit or delete a message unilaterally. Deletion requires all participants to agree, or the thread owner to close it. When deleted, a full quantum-encrypted message history is emailed to every participant as a downloadable JSON archive.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/threads-documentation",
      docLabel: "Threads documentation →",
    },
  },
  {
    id: "n-threads-verify",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Verify Thread Archive",
      icon: ShieldCheckIcon,
      variant: "thread",
      description:
        "Anyone who received the deletion archive can verify its integrity at the Majikah validator. The download link expires 72 hours after deletion.",
      example: "https://message.majikah.solutions/threads/validate",
      docLink: "https://message.majikah.solutions/threads/validate",
      docLabel: "Open validator →",
    },
  },
  {
    id: "n-inbox",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Message Arrives at Recipient",
      icon: InboxIcon,
      variant: "transport",
      description:
        "The Majik Message scanner detects the ~*$MJKMSG: prefix anywhere — in the app, Chrome extension, or pasted directly. It extracts and reconstructs the v3 encrypted envelope, ready for quantum-safe decryption.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/chats-realtime-documentation#scanner",
      docLabel: "How the scanner works →",
    },
  },

  // ⑦ Decryption
  {
    id: "sec-decrypt",
    type: "sectionLabel",
    position: { x: 0, y: 0 },
    data: {
      label: "⑦ Post-Quantum Decryption",
      color: COLORS.accent.gold,
    },
    selectable: false,
    draggable: false,
  },
  {
    id: "n-unlock",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Unlock Your Private Keys",
      icon: LockLaminatedOpenIcon,
      step: "Decrypt",
      variant: "decrypt",
      description:
        "You enter your passphrase. Argon2id derives the AES key (64 MB memory-hard KDF, resistant to GPU attacks). Both your X25519 private key and ML-KEM-768 secret key are decrypted and loaded into memory — only for this session.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/accounts-documentation#unlock",
      docLabel: "Unlock flow →",
    },
  },
  {
    id: "n-match",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Find Your Key Entry",
      icon: IdentificationCardIcon,
      variant: "decrypt",
      description:
        "For solo messages: the envelope fingerprint identifies you directly. For group messages: the app scans the payload's keys array and finds the entry with your fingerprint. Each entry has a unique 1088-byte ML-KEM ciphertext.",
    },
  },
  {
    id: "n-mlkem-decap",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "ML-KEM Decapsulation",
      icon: HandshakeIcon,
      step: "Quantum-Safe",
      variant: "decrypt",
      description:
        "Your ML-KEM-768 secret key (2400 bytes) decapsulates the 1088-byte ciphertext, recovering the 32-byte shared secret. For group messages: XOR the shared secret with encryptedAesKey to recover the group AES key.",
      example:
        "mlKemDecapsulate(cipherText_1088, secretKey_2400) → sharedSecret_32",
      docLink: "https://csrc.nist.gov/pubs/fips/203/final",
      docLabel: "FIPS-203 (ML-KEM) →",
    },
  },
  {
    id: "n-aes-decrypt",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Decrypt & Verify Integrity",
      icon: LockOpenIcon,
      variant: "decrypt",
      description:
        "AES-256-GCM decrypts the message and simultaneously verifies the tamper-proof seal. If anyone modified the message — even by a single byte — decryption returns nothing. Tampering is cryptographically impossible to hide.",
      example: "gcm.open(sharedSecret, iv, ciphertext) → plaintext or null",
    },
  },
  {
    id: "n-decompress",
    type: "majikNode",
    position: { x: 0, y: 0 },
    data: {
      label: "Message Delivered",
      icon: ChatsIcon,
      step: "Done",
      variant: "end",
      hideSource: true,
      description:
        "Compressed messages are decompressed and displayed. Your ML-KEM secret key is wiped from memory when the session ends — it is never persisted unencrypted. Your messages are quantum-safe: even a future quantum computer cannot break this encryption.",
      docLink:
        "https://majikah.solutions/products/majik-message/docs/chats-realtime-documentation",
      docLabel: "Full message flow docs →",
    },
  },
];

// ─── Layout Descriptor ────────────────────────────────────────────────────────

const LAYOUT: LayoutEntry[] = [
  // ① Account Setup
  { type: "section", id: "sec-onboard" },
  { type: "node", id: "n-start" },
  { type: "node", id: "n-choose" },
  { type: "branch", left: "n-seed", right: "n-import" },
  { type: "branch", left: "n-seed-backup", right: "n-import-verify" },
  { type: "gap", px: 15 },

  // ② Key Derivation (updated: dual keypair)
  { type: "section", id: "sec-keygen" },
  { type: "node", id: "n-mnemonic-seed" },
  { type: "node", id: "n-dual-derivation" },
  { type: "branch", left: "n-x25519-path", right: "n-mlkem-path" },
  { type: "node", id: "n-argon2id-encrypt" },
  { type: "gap", px: 15 },

  // ③ Identity
  { type: "section", id: "sec-identity" },
  { type: "node", id: "n-fingerprint" },
  { type: "node", id: "n-contact-card" },
  { type: "node", id: "n-local-ready" },
  { type: "node", id: "n-register", col: "right" },
  { type: "gap", px: 15 },

  // ④ Compose
  { type: "section", id: "sec-compose" },
  { type: "node", id: "n-compose" },
  { type: "node", id: "n-resolve" },
  { type: "gap", px: 15 },

  // ⑤ Encryption (updated: ML-KEM only)
  { type: "section", id: "sec-encrypt" },
  { type: "node", id: "n-envelope-version" },
  { type: "branch", left: "n-solo-mlkem", right: "n-group-aeskey" },
  { type: "branch", left: "n-solo-encrypt", right: "n-group-perrecip" },
  { type: "node", id: "n-aes-encrypt" },
  { type: "gap", px: 15 },

  // ⑥ Transport
  { type: "section", id: "sec-transport" },
  { type: "node", id: "n-envelope" },
  { type: "branch", left: "n-route-relay", right: "n-route-p2p" },
  { type: "node", id: "n-threads", col: "right" },
  { type: "node", id: "n-threads-verify", col: "right" },
  { type: "node", id: "n-inbox" },
  { type: "gap", px: 15 },

  // ⑦ Decryption (updated: ML-KEM decapsulation)
  { type: "section", id: "sec-decrypt" },
  { type: "node", id: "n-unlock" },
  { type: "node", id: "n-match" },
  { type: "node", id: "n-mlkem-decap" },
  { type: "node", id: "n-aes-decrypt" },
  { type: "node", id: "n-decompress" },
];

// ─── Edge Helpers ─────────────────────────────────────────────────────────────

type EdgeStyle = { stroke: string; strokeWidth: number };

function mkEdge(
  id: string,
  source: string,
  target: string,
  style: EdgeStyle,
  opts: Partial<Edge> = {},
): Edge {
  return {
    id,
    source,
    target,
    animated: true,
    style,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 16,
      height: 16,
      color: style.stroke,
    },
    ...opts,
  };
}

const S = {
  orange: { stroke: COLORS.accent.orange, strokeWidth: 2 },
  cyan: { stroke: COLORS.accent.cyan, strokeWidth: 2 },
  emerald: { stroke: COLORS.accent.emerald, strokeWidth: 2 },
  amber: { stroke: COLORS.accent.amber, strokeWidth: 2 },
  rose: { stroke: COLORS.accent.rose, strokeWidth: 2 },
  sky: { stroke: COLORS.accent.sky, strokeWidth: 2 },
  gold: { stroke: COLORS.accent.gold, strokeWidth: 2 },
  violet: { stroke: COLORS.accent.violet, strokeWidth: 2 },
} satisfies Record<string, EdgeStyle>;

// ─── Updated Edges ────────────────────────────────────────────────────────────

const INITIAL_EDGES: Edge[] = [
  // ① Setup
  mkEdge("e-start-choose", "n-start", "n-choose", S.orange),
  mkEdge("e-choose-seed", "n-choose", "n-seed", S.orange, {
    label: "New",
    type: "smoothstep",
  }),
  mkEdge("e-choose-import", "n-choose", "n-import", S.orange, {
    label: "Import",
    type: "smoothstep",
  }),
  mkEdge("e-seed-backup", "n-seed", "n-seed-backup", S.cyan),
  mkEdge("e-import-verify", "n-import", "n-import-verify", S.cyan),

  // Both paths converge → key derivation
  mkEdge("e-backup-mnemonic", "n-seed-backup", "n-mnemonic-seed", S.cyan, {
    type: "smoothstep",
  }),
  mkEdge("e-verify-mnemonic", "n-import-verify", "n-mnemonic-seed", S.cyan, {
    type: "smoothstep",
  }),

  // ② Key derivation — dual keypair split
  mkEdge("e-mnemonic-dual", "n-mnemonic-seed", "n-dual-derivation", S.cyan),
  mkEdge("e-dual-x25519", "n-dual-derivation", "n-x25519-path", S.cyan, {
    label: "seed[0..32]",
    type: "smoothstep",
  }),
  mkEdge("e-dual-mlkem", "n-dual-derivation", "n-mlkem-path", S.cyan, {
    label: "seed[0..64]",
    type: "smoothstep",
  }),

  // Both keys converge → Argon2id encryption
  mkEdge("e-x25519-argon", "n-x25519-path", "n-argon2id-encrypt", S.cyan, {
    type: "smoothstep",
  }),
  mkEdge("e-mlkem-argon", "n-mlkem-path", "n-argon2id-encrypt", S.cyan, {
    type: "smoothstep",
  }),

  // ③ Identity
  mkEdge("e-argon-fp", "n-argon2id-encrypt", "n-fingerprint", S.emerald),
  mkEdge("e-fp-card", "n-fingerprint", "n-contact-card", S.emerald),
  mkEdge("e-card-local", "n-contact-card", "n-local-ready", S.emerald),
  mkEdge("e-local-register", "n-local-ready", "n-register", S.emerald, {
    label: "optional",
    type: "smoothstep",
  }),
  mkEdge("e-register-compose", "n-register", "n-compose", S.emerald, {
    type: "smoothstep",
  }),

  // ④ Compose
  mkEdge("e-local-compose", "n-local-ready", "n-compose", S.amber),
  mkEdge("e-compose-resolve", "n-compose", "n-resolve", S.amber),

  // ⑤ Encryption — v3 ML-KEM only
  mkEdge("e-resolve-version", "n-resolve", "n-envelope-version", S.rose),
  mkEdge("e-version-solo", "n-envelope-version", "n-solo-mlkem", S.rose, {
    label: "1 recipient",
    type: "smoothstep",
  }),
  mkEdge("e-version-group", "n-envelope-version", "n-group-aeskey", S.rose, {
    label: "2+ recipients",
    type: "smoothstep",
  }),
  mkEdge("e-solo-mlkem-encrypt", "n-solo-mlkem", "n-solo-encrypt", S.rose),
  mkEdge("e-solo-aes", "n-solo-encrypt", "n-aes-encrypt", S.rose, {
    type: "smoothstep",
  }),
  mkEdge("e-group-perrecip", "n-group-aeskey", "n-group-perrecip", S.rose),
  mkEdge("e-group-aes", "n-group-perrecip", "n-aes-encrypt", S.rose, {
    type: "smoothstep",
  }),

  // ⑥ Transport
  mkEdge("e-aes-envelope", "n-aes-encrypt", "n-envelope", S.sky),
  mkEdge("e-envelope-relay", "n-envelope", "n-route-relay", S.sky, {
    label: "offline",
    type: "smoothstep",
  }),
  mkEdge("e-envelope-p2p", "n-envelope", "n-route-p2p", S.sky, {
    label: "online",
    type: "smoothstep",
  }),
  mkEdge("e-p2p-threads", "n-route-p2p", "n-threads", S.violet, {
    type: "smoothstep",
  }),
  mkEdge("e-threads-verify", "n-threads", "n-threads-verify", S.violet),
  mkEdge("e-relay-inbox", "n-route-relay", "n-inbox", S.sky, {
    type: "smoothstep",
  }),
  mkEdge("e-p2p-inbox", "n-route-p2p", "n-inbox", S.sky, {
    type: "smoothstep",
  }),
  mkEdge("e-threads-verify-inbox", "n-threads-verify", "n-inbox", S.sky, {
    type: "smoothstep",
  }),

  // ⑦ Decryption — ML-KEM decapsulation
  mkEdge("e-inbox-unlock", "n-inbox", "n-unlock", S.gold),
  mkEdge("e-unlock-match", "n-unlock", "n-match", S.gold),
  mkEdge("e-match-mlkem", "n-match", "n-mlkem-decap", S.gold),
  mkEdge("e-mlkem-aes", "n-mlkem-decap", "n-aes-decrypt", S.gold),
  mkEdge("e-aes-decompress", "n-aes-decrypt", "n-decompress", S.gold),
];

// ─── Legend ───────────────────────────────────────────────────────────────────

const LEGEND: [AccentKey, string][] = [
  ["orange", "Setup"],
  ["cyan", "Key Gen"],
  ["emerald", "Identity"],
  ["amber", "Compose"],
  ["rose", "Encrypt"],
  ["sky", "Transport"],
  ["violet", "Threads"],
  ["gold", "Decrypt"],
];

// ─── Canvas Height Calculation ────────────────────────────────────────────────

const CANVAS_BOTTOM_PAD = 250;
const HEADER_H = 74;

function computeExpandHeight(nodes: AnyFlowNode[]): number {
  const maxBottom = nodes.reduce(
    (acc, n) => Math.max(acc, n.position.y + NODE_H),
    0,
  );
  return maxBottom + CANVAS_BOTTOM_PAD + HEADER_H;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface MajikMessageHowItWorksChartProps {
  expand?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MajikMessageHowItWorksChart({
  expand = false,
}: MajikMessageHowItWorksChartProps) {
  const isMobile = useIsMobile();

  const [nodes, setNodes] = useState<AnyFlowNode[]>(() =>
    layoutNodes(LAYOUT, RAW_NODES, false),
  );
  const [edges, setEdges] = useState<Edge[]>(INITIAL_EDGES);

  // Only update desktop layout on resize (mobile uses its own renderer entirely)
  useEffect(() => {
    if (!isMobile) {
      setNodes(layoutNodes(LAYOUT, RAW_NODES, false));
    }
  }, [isMobile]);

  const onNodesChange = useCallback(
    (changes: NodeChange<AnyFlowNode>[]) =>
      setNodes((prev) => applyNodeChanges(changes, prev)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) =>
      setEdges((prev) => applyEdgeChanges(changes, prev)),
    [],
  );

  const onConnect = useCallback(
    (params: Connection) => setEdges((prev) => addEdge(params, prev)),
    [],
  );

  const expandCanvasHeight = useMemo(
    () => (expand && !isMobile ? computeExpandHeight(nodes) : undefined),
    [expand, isMobile, nodes],
  );

  const header = (
    <Header>
      <HeaderLeft>
        <Logo>
          <ResponsiveImage
            src="https://storage.majikah.solutions/public/Logo_MajikMessage_Majikah_Thumbnail_WEBP.webp?url"
            alt="Majik Message Logo"
            height={250}
            style={{ width: "100%" }}
            priority
            width={250}
          />
        </Logo>
        <div>
          <BrandTitle>Majik Message</BrandTitle>
          <BrandSubtitle>
            HOW IT WORKS — POST-QUANTUM ENCRYPTED MESSAGING
          </BrandSubtitle>
        </div>
      </HeaderLeft>

      <LegendRow>
        {LEGEND.map(([colorKey, label]) => (
          <LegendItem key={label}>
            <LegendDot $color={COLORS.accent[colorKey]} />
            <LegendLabel>{label}</LegendLabel>
          </LegendItem>
        ))}
      </LegendRow>
    </Header>
  );

  return (
    <>
      <GlobalFonts />
      <RootContainer>
        {header}

        {/* ── Mobile: plain scrollable card list, no ReactFlow at all ── */}
        {isMobile && <MobileLayout rawNodes={RAW_NODES} />}

        {/* ── Desktop: full ReactFlow canvas ── */}
        {!isMobile && (
          <CanvasLayer $height={expandCanvasHeight}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={NODE_TYPES}
              fitView
              fitViewOptions={{
                padding: 0.01,
                includeHiddenNodes: false,
                nodes: nodes,
              }}
              colorMode="dark"
              defaultEdgeOptions={{ animated: true }}
              preventScrolling={!expand}
              zoomOnScroll={!expand}
              zoomOnPinch={!expand}
              zoomOnDoubleClick={!expand}
              panOnDrag={!expand}
              panOnScroll={false}
              selectionOnDrag={false}
              nodesDraggable={!expand}
              nodesConnectable={!expand}
              elementsSelectable={!expand}
              minZoom={expand ? 1 : 0.1}
              maxZoom={expand ? 1 : 4}
              proOptions={{ hideAttribution: true }}
            >
              <Background
                variant={BackgroundVariant.Dots}
                gap={28}
                color={COLORS.border}
                size={1.2}
                bgColor={COLORS.bg}
              />
              {!expand && (
                <Controls
                  style={{
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                  }}
                />
              )}
              {!expand && (
                <MiniMap
                  nodeColor={(n) => {
                    const v = (n.data as MajikNodeData).variant as
                      | VariantKey
                      | undefined;
                    return v && VARIANTS[v] ? VARIANTS[v].border : "#4080c0";
                  }}
                  maskColor="rgba(10,10,20,0.88)"
                  style={{
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 10,
                  }}
                />
              )}
            </ReactFlow>
          </CanvasLayer>
        )}
      </RootContainer>
    </>
  );
}
